import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from 'core/components';
import styled from 'styled-components';
import { diffLines } from 'diff';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/markdown/markdown';
import { CodeViewerContainer } from './sharedStyles';

const DiffCodeViewerContainer = styled(CodeViewerContainer)`
  .diff-add-text {
    background-color: rgba(46, 160, 67, 0.25) !important;
  }
  .diff-sub-text {
    background-color: rgba(248, 81, 73, 0.25) !important;
  }
  .diff-add-symbol {
    color: #3fb950 !important;
    font-weight: bold;
  }
  .diff-sub-symbol {
    color: #f85149 !important;
    font-weight: bold;
  }
`;

export const buildUnifiedDiff = (oldText, newText) =>
  diffLines(oldText || '', newText || '')
    .flatMap((part) => {
      let prefix = ' ';
      if (part.added) {
        prefix = '+';
      } else if (part.removed) {
        prefix = '-';
      }

      return part.value
        .replace(/\n$/, '')
        .split('\n')
        .map((l) => (l ? `${prefix} ${l}` : prefix));
    })
    .join('\n');

const MarkdownCodeViewer = ({ value, mode, isDiff, minHeight, maxHeight }) => {
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.getAllMarks().forEach((mark) => mark.clear());

    if (!isDiff) {
      return;
    }

    editor.eachLine((line) => {
      const { text } = line;
      const lineNo = editor.getLineNumber(line);

      if (text.startsWith('+ ') || text === '+') {
        const symbolLen = text === '+' ? 1 : 2;
        editor.markText({ line: lineNo, ch: 0 }, { line: lineNo, ch: text.length }, { className: 'diff-add-text' });
        editor.markText({ line: lineNo, ch: 0 }, { line: lineNo, ch: symbolLen }, { className: 'diff-add-symbol' });
      } else if (text.startsWith('- ') || text === '-') {
        const symbolLen = text === '-' ? 1 : 2;
        editor.markText({ line: lineNo, ch: 0 }, { line: lineNo, ch: text.length }, { className: 'diff-sub-text' });
        editor.markText({ line: lineNo, ch: 0 }, { line: lineNo, ch: symbolLen }, { className: 'diff-sub-symbol' });
      }
    });
  }, [editor, value, isDiff]);

  const Container = isDiff ? DiffCodeViewerContainer : CodeViewerContainer;

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <Container $minHeight={minHeight} $maxHeight={maxHeight}>
        <CodeMirror
          value={value}
          options={{
            mode,
            lineNumbers: true,
            readOnly: 'nocursor',
            theme: 'default',
            lineWrapping: true
          }}
          editorDidMount={(ed) => setEditor(ed)}
        />
      </Container>
    </Box>
  );
};

MarkdownCodeViewer.propTypes = {
  value: PropTypes.string,
  mode: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isDiff: PropTypes.bool,
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string
};

MarkdownCodeViewer.defaultProps = {
  value: '',
  mode: 'markdown',
  isDiff: false,
  minHeight: '350px',
  maxHeight: '65vh'
};

export default MarkdownCodeViewer;
